// WHERE A VISIT CAME FROM, in six buckets — and one of them is the point.
//
// capnolog.com is not indexed by Google, Bing or Brave today and no assistant
// names the product (docs/ops/ai-visibility.md). The panel measures what the
// assistants SAY; this module measures whether that ever turns into a visit.
// A referral from chatgpt.com is not "other traffic" — it is the first evidence
// that being named is worth anything, and it has to be a named class before it
// can be a chart.
//
// PURE, and deliberately import-free: `src/hooks/usePageView.ts` calls it in the
// browser and `__tests__/referrer-class.test.mjs` imports this file straight
// from TypeScript through Node's type stripping, the same way public-url.ts is
// read. Nothing here may import anything, and nothing here may reach for
// `window` — the caller supplies both inputs.
//
// This file is the source of truth for the host lists. docs/ops/web-funnel.md
// § "AI-assistant referrals" describes them; PostHog's own channel-type rule is
// a SECOND copy the owner maintains in the PostHog UI, and it is documented as
// such rather than derived from here.

export type ReferrerClass =
  | "ai_assistant"
  | "search"
  | "social"
  | "direct"
  | "internal"
  | "other";

/**
 * Assistant surfaces. `openai.com` is here as well as `chatgpt.com` because a
 * shared conversation link still resolves on the older host, and
 * `gemini.google.com` / `bard.google.com` must be tested BEFORE the `google.*`
 * search wildcard or Gemini reads as organic search.
 */
export const AI_ASSISTANT_HOSTS: readonly string[] = [
  "chatgpt.com",
  "chat.openai.com",
  "openai.com",
  "perplexity.ai",
  "www.perplexity.ai",
  "claude.ai",
  "gemini.google.com",
  "bard.google.com",
  "copilot.microsoft.com",
  "you.com",
  "meta.ai",
  "chat.mistral.ai",
  "grok.com",
  "poe.com",
  "phind.com",
  "chat.deepseek.com",
];

/**
 * `utm_source` values that mean "an assistant sent them" no matter what host
 * the browser reported — an assistant that strips the referrer, or a link the
 * answer tagged itself, still counts.
 *
 * These are compared AFTER normalisation, so both the raw `chatgpt.com` and the
 * `chatgpt-com` that `slugify()` in analytics.ts produces from it match. That
 * matters: by the time usePageView calls this, the value HAS been slugified.
 */
export const AI_ASSISTANT_UTM_SOURCES: readonly string[] = [
  "chatgpt.com",
  "chatgpt",
  "openai",
  "perplexity",
  "claude",
  "gemini",
  "copilot",
];

/** Search engines with a fixed host. `google.*` / `yandex.*` are below. */
export const SEARCH_HOSTS: readonly string[] = [
  "bing.com",
  "duckduckgo.com",
  "search.brave.com",
  "search.yahoo.com",
  "ecosia.org",
  "kagi.com",
  "baidu.com",
];

/**
 * Search engines whose country domains are unbounded — google.com, google.co.uk,
 * google.de, yandex.ru, yandex.com.tr. Matched as `<name>.<tld>` or
 * `<name>.<tld>.<tld>` with any number of leading subdomains, which is narrow
 * enough that `google.com.example.net` does NOT read as Google.
 */
export const SEARCH_HOST_WILDCARDS: readonly string[] = ["google", "yandex"];

/** Social and community. Includes the link shorteners each one redirects through. */
export const SOCIAL_HOSTS: readonly string[] = [
  "reddit.com",
  "old.reddit.com",
  "t.co",
  "x.com",
  "twitter.com",
  "linkedin.com",
  "lnkd.in",
  "facebook.com",
  "l.facebook.com",
  "m.facebook.com",
  "instagram.com",
  "l.instagram.com",
  "youtube.com",
  "youtu.be",
  "news.ycombinator.com",
  "producthunt.com",
  "forums.studentdoctor.net",
  "tiktok.com",
  "threads.net",
];

export interface ReferrerInput {
  /**
   * The value `usePageView`'s `referrerHost()` produced: a bare host, or one of
   * the sentinels "direct" / "internal" / "unknown".
   */
  readonly referrerHost: string;
  /** The slugified `utm_source`, or "none". Optional so a caller may omit it. */
  readonly utmSource?: string | null;
}

/** Lowercase, trim, drop a trailing dot and a leading "www." is NOT dropped — see matchesHost. */
function normaliseHost(value: string | null | undefined): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\.$/, "");
}

/** The same shape `slugify()` in analytics.ts produces, so both forms compare equal. */
function normaliseUtm(value: string | null | undefined): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Exact host, or any subdomain of it. `l.facebook.com` matches `facebook.com`. */
function matchesHost(host: string, list: readonly string[]): boolean {
  for (const entry of list) {
    if (host === entry || host.endsWith(`.${entry}`)) return true;
  }
  return false;
}

/** `google.com`, `www.google.com`, `google.co.uk` — but not `google.com.evil.net`. */
function matchesWildcard(host: string, names: readonly string[]): boolean {
  for (const name of names) {
    const re = new RegExp(`(^|\\.)${name}\\.[a-z]{2,}(\\.[a-z]{2,})?$`);
    if (re.test(host)) return true;
  }
  return false;
}

/**
 * The bucket this visit belongs in. Order is load-bearing:
 *
 *  1. the utm_source override, because an assistant that strips the referrer
 *     would otherwise land in "direct" and disappear;
 *  2. the two sentinels, which are not hosts at all;
 *  3. ai_assistant BEFORE search, so `gemini.google.com` is not Google;
 *  4. search, social, then everything else.
 *
 * "unknown" (a referrer URL that would not parse) falls through to "other" —
 * it is not "direct", and pretending it is would flatter the direct number.
 */
export function classifyReferrer(input: ReferrerInput): ReferrerClass {
  const utm = normaliseUtm(input.utmSource);
  if (utm) {
    for (const source of AI_ASSISTANT_UTM_SOURCES) {
      if (normaliseUtm(source) === utm) return "ai_assistant";
    }
  }

  const host = normaliseHost(input.referrerHost);
  if (host === "direct") return "direct";
  if (host === "internal") return "internal";
  if (!host) return "other";

  if (matchesHost(host, AI_ASSISTANT_HOSTS)) return "ai_assistant";
  if (matchesHost(host, SEARCH_HOSTS)) return "search";
  if (matchesWildcard(host, SEARCH_HOST_WILDCARDS)) return "search";
  if (matchesHost(host, SOCIAL_HOSTS)) return "social";
  return "other";
}
